import React, { useEffect } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Grid, Stack } from '@mui/material'
import { useTheme } from '@mui/styles'
import Input from 'components/generic/form/Input'
import { formatNumber, formatNumberForDisplay } from 'utils/helpers'

const InvoiceFooter = ({ summaryData, canEditNotes, pricePrepend }) => {
  const theme = useTheme()
  const invoiceMethods = useFormContext()
  const summaryDataMethods = useForm()

  const { defaultYardUm } = useSelector(state => state.settings)

  const inputFields = [
    { name: 'grossWeight', label: 'Gross:' },
    { name: 'tareWeight', label: 'Tare:' },
    { name: 'netWeight', label: 'Net:' },
    { name: 'freightValue', label: 'Freight:' },
    { name: 'subTotalValue', label: 'Subtotal:' },
    { name: 'taxValue', label: 'Tax:' },
    { name: 'totalValue', label: 'Total:' },
  ]

  useEffect(() => {
    const netWeight = summaryData.grossWeight - summaryData.tareWeight || 0
    summaryDataMethods.setValue('grossWeight', `${formatNumber(summaryData.grossWeight)} ${defaultYardUm}`)
    summaryDataMethods.setValue('tareWeight', `${formatNumber(summaryData.tareWeight)} ${defaultYardUm}`)
    summaryDataMethods.setValue('netWeight', `${formatNumber(netWeight)} ${defaultYardUm}`)
    summaryDataMethods.setValue(
      'freightValue',
      formatNumberForDisplay(summaryData.freightValue, {
        format: 'price',
        decimalPrecisionOverride: 2,
        currencySymbol: pricePrepend,
      }),
    )
    summaryDataMethods.setValue(
      'subTotalValue',
      formatNumberForDisplay(summaryData.subTotal, {
        format: 'price',
        decimalPrecisionOverride: 2,
        currencySymbol: pricePrepend,
      }),
    )
    summaryDataMethods.setValue(
      'taxValue',
      formatNumberForDisplay(summaryData.taxValue, {
        format: 'price',
        decimalPrecisionOverride: 2,
        currencySymbol: pricePrepend,
      }),
    )
    summaryDataMethods.setValue(
      'totalValue',
      formatNumberForDisplay(summaryData.totalValue, {
        format: 'price',
        decimalPrecisionOverride: 2,
        currencySymbol: pricePrepend,
      }),
    )
  }, [summaryData, defaultYardUm, pricePrepend])

  return (
    <>
      <Grid
        sx={{
          padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
          bottom: 0,
          zIndex: 2,
          position: 'sticky',
          background: theme.palette.background.default,
        }}
        borderTop={1}
        borderColor="divider"
      >
        <Stack direction="row" alignItems="center" spacing={theme.spacing(1)}>
          <Input
            name="details.notes"
            label="Invoice Notes"
            disabled={!canEditNotes}
            data-test="details-notes"
            control={invoiceMethods.control}
            error={!!invoiceMethods.errors?.details?.notes}
            helperText={invoiceMethods.errors?.details?.notes?.message}
            defaultValue=""
            sx={{
              flex: 4,
              paddingRight: theme.spacing(3),
            }}
          />
          {inputFields.map(field => (
            <Stack sx={{ flex: 1 }} key={field?.name}>
              <Input
                name={field?.name}
                label={field?.label}
                control={summaryDataMethods.control}
                variant="standard"
                defaultValue=""
                sx={{
                  '& .MuiInput-root': {
                    cursor: 'unset',
                  },
                  input: {
                    WebkitTextFillColor: theme.palette.text.primary,
                    fontWeight: 500,
                    width: `${summaryDataMethods.getValues(field?.name)?.length || 0}ch`,
                    cursor: 'text',
                  },
                  label: {
                    WebkitTextFillColor: theme.palette.text.secondary,
                  },
                }}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          ))}
        </Stack>
      </Grid>
    </>
  )
}

InvoiceFooter.propTypes = {
  summaryData: PropTypes.object.isRequired,
  canEditNotes: PropTypes.bool,
  pricePrepend: PropTypes.string,
}

InvoiceFooter.defaultProps = {
  canEditNotes: false,
  pricePrepend: '$',
}

export default InvoiceFooter
